/*eslint no-param-reassign: "error"*/
import React, { useContext, useEffect, useState } from 'react';
import { ScorecardStepper } from './ScorecardStepper';
import checkIcon from '../../assets/checkIcon.png';
import closedIcon from '../../assets/closedIcon.png';
import haltedIcon from '../../assets/haltedIcon.png';
import startIcon from '../../assets/startIcon.png';
import { ConstApprovalWindowStatus, StepperSteps } from '../../utils/typeConstants';
import { useGetPrimaryApprovalWindowQuery } from '../../pages/compensation/compensationSlice';
import { TLLandingContext } from '../../features/landingPage/teamLeadLandingPage/TeamLeadLandingPage';

interface ScorecardPizzaTrackerProps {
    inputMonth: number;
    inputYear: number;
    inputScorecardVersion: number | undefined;
    fromCompensation?: boolean;
}

export const ScorecardPizzaTracker = ({
    inputMonth,
    inputYear,
    inputScorecardVersion,
    fromCompensation,
}: ScorecardPizzaTrackerProps) => {
    const { viaPage } = useContext(TLLandingContext);
    const [stepperSteps, setStepperSteps] = useState<any[]>([]);
    const [isHeaderShow, setIsHeaderShow] = useState<boolean>(false);

    const { data: primaryApprovalWindowData } = useGetPrimaryApprovalWindowQuery(
        {
            year: inputYear,
            month: inputMonth,
            scorecardVersionId: inputScorecardVersion,
            approvalWindowConfigLevelId: 3,
        },
        { skip: !inputYear || !inputMonth }
    );

    const getApprovalPct = (
        activeStep: number,
        assocApprovePct: number,
        totalTLApprovePct: number,
        mgrApprovePct: number
    ) => {
        if (activeStep === ConstApprovalWindowStatus.ASSOCIATE_WINDOW) return assocApprovePct;
        else if (activeStep === ConstApprovalWindowStatus.TEAM_LEAD_WINDOW) return totalTLApprovePct;
        else if (activeStep === ConstApprovalWindowStatus.MANAGER_WINDOW) return mgrApprovePct;
        else return 0;
    };

    const isApprovalWindowStarted = (approvalWindowData: string) => {
        return new Date(new Date().toDateString()) >= new Date(approvalWindowData);
    };

    useEffect(() => {
        if (
            primaryApprovalWindowData?.data?.length > 0 &&
            isApprovalWindowStarted(primaryApprovalWindowData?.data[0]?.associate_start_date)
        ) {
            const initialStepperSteps = JSON.parse(JSON.stringify(StepperSteps));
            const activeStep = primaryApprovalWindowData?.data[0]?.approval_window_status_id;
            const stepStalled = primaryApprovalWindowData?.data[0]?.approvals_paused;
            const assocApprovePct = primaryApprovalWindowData?.data[0]?.assoc_approve_pct;
            const totalTLApprovePct = primaryApprovalWindowData?.data[0]?.tl_total_approve_pct;
            const mgrApprovePct = primaryApprovalWindowData?.data[0]?.mgr_approve_pct;

            if (activeStep === ConstApprovalWindowStatus.APPROVALS_HALTED) {
                initialStepperSteps[0].isHalted = true;
                initialStepperSteps[0].inProcess = true;
                initialStepperSteps[0].colorCode = '#E23636';
                initialStepperSteps[0].icon = haltedIcon;
                initialStepperSteps[0].subHeading = 'Not Started';
                initialStepperSteps[0].stepId = activeStep;
                setStepperSteps(initialStepperSteps);
            } else {
                initialStepperSteps.forEach((step: any) => {
                    if (step.stepId === activeStep) {
                        if (activeStep === ConstApprovalWindowStatus.APPROVALS_NOT_STARTED) {
                            step.inProcess = true;
                            step.colorCode = stepStalled ? '#E23636' : '#2AA154';
                            step.isStalled = stepStalled;
                            step.subHeading = stepStalled ? 'Not Started Stalled' : 'In Process';
                            step.icon = startIcon;
                        } else if (activeStep === ConstApprovalWindowStatus.CLOSED) {
                            step.subHeading = 'Complete';
                            step.inProcess = true;
                            step.colorCode = '#2AA154';
                            step.icon = closedIcon;
                        } else {
                            step.inProcess = true;
                            step.colorCode = stepStalled ? '#E23636' : '#1A5AA5';
                            step.isStalled = stepStalled;
                            step.subHeading = stepStalled ? 'In Process Stalled' : 'In Process';
                            if (
                                activeStep === ConstApprovalWindowStatus.COMPENSATION_TEAM_WINDOW ||
                                activeStep === ConstApprovalWindowStatus.READY_FOR_EXTRACT
                            ) {
                                step.icon = startIcon;
                            } else {
                                step.pct = getApprovalPct(
                                    activeStep,
                                    assocApprovePct,
                                    totalTLApprovePct,
                                    mgrApprovePct
                                );
                            }
                        }
                    } else if (step.stepId < activeStep) {
                        step.subHeading = 'Complete';
                        step.inProcess = false;
                        step.colorCode = '#2AA154';
                        step.icon = checkIcon;
                        if (step.stepId === ConstApprovalWindowStatus.APPROVALS_NOT_STARTED) {
                            step.icon = startIcon;
                            step.subHeading =
                                activeStep !== ConstApprovalWindowStatus.CLOSED ? 'In Process' : 'Complete';
                        } else if (step.stepId === ConstApprovalWindowStatus.CLOSED) step.icon = closedIcon;
                    }
                });
                setStepperSteps(initialStepperSteps);
            }
            setIsHeaderShow(true);
        } else {
            setIsHeaderShow(false);
            setStepperSteps([]);
        }
    }, [primaryApprovalWindowData]);

    return (
        <>
            {viaPage === 'TLLandingPage' ? '' : !fromCompensation && isHeaderShow && <h6>Scorecard Status</h6>}
            <div className="mt-4" style={{ display: 'flex', justifyContent: 'center' }}>
                {stepperSteps.map((step, index) => {
                    return (
                        <ScorecardStepper
                            key={index}
                            step={step}
                            isEndStep={index === stepperSteps.length - 1}
                            activeStep={primaryApprovalWindowData?.data[0]?.approval_window_status_id}
                        />
                    );
                })}
            </div>
        </>
    );
};
